import { Badge, Button, EmptyState, Skeleton, UrgencyBadge } from "@omony/ui";
import { ClipboardList, Plus, Receipt, Users } from "lucide-react";
import { useIntl } from "react-intl";
import { joinParts, SEP } from "../shared/format.js";
import { navigate } from "../shared/hashRoute.js";
import { useOpenCases } from "./data.js";
import { useCaseLabel, useT } from "./flow/common.js";
import styles from "./flow/flow.module.css";

/** Startseite: „Neuer Vorgang“ und offene Vorgänge zum Fortsetzen */
export function Home({ mode }: { mode: "staff" | "account" }) {
  const t = useT();
  const intl = useIntl();
  const cases = useOpenCases();
  const label = useCaseLabel();
  const items = cases.data?.items ?? [];
  return (
    <div className={styles.main}>
      <section className={styles.hero} aria-labelledby="home-title">
        <div>
          <h1 id="home-title" className={styles.heroTitle}>
            {t("console.home.title")}
          </h1>
          <p className={styles.heroText}>{t("console.home.lead")}</p>
        </div>
        <Button size="lg" icon={<Plus />} onPress={() => navigate("neu")}>
          {t("console.home.newCase")}
        </Button>
      </section>

      <section className={styles.section} aria-labelledby="open-title">
        <h2 id="open-title" className={styles.sectionTitle}>
          {t("console.home.open")}
        </h2>
        {cases.isLoading ? (
          <Skeleton lines={3} />
        ) : items.length === 0 ? (
          <EmptyState icon={<ClipboardList />} title={t("console.home.noOpen")} />
        ) : (
          <ul className={styles.caseList}>
            {items.map((c) => (
              <li key={c.id} className={styles.caseRow}>
                <div className={styles.caseRowMain}>
                  <strong>{label(c)}</strong>
                  <span className={styles.small}>
                    <span className={styles.mono}>{c.reference}</span>
                    {SEP}
                    {joinParts(
                      c.signageNumber !== null && t("console.case.number", { n: c.signageNumber }),
                      intl.formatTime(c.times.createdAt),
                    )}
                  </span>
                </div>
                {c.urgency ? <UrgencyBadge urgency={c.urgency} /> : null}
                <Badge tone={c.status === "wartend" ? "warning" : "accent"}>
                  {t(`case.status.${c.status}`)}
                </Badge>
                {c.openVitalRequests > 0 ? <Badge tone="danger">{t("console.home.vitalsOpen")}</Badge> : null}
                {c.billingStatus === "offen" ? (
                  <Badge tone="neutral">{t("console.home.billingOpen")}</Badge>
                ) : null}
                <Button variant="secondary" onPress={() => navigate(`vorgang/${c.id}`)}>
                  {t("console.home.resume")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {mode === "account" ? (
        <section className={styles.section} aria-labelledby="acc-title">
          <h2 id="acc-title" className={styles.sectionTitle}>
            {t("console.home.account")}
          </h2>
          <div className={styles.actions}>
            <Button variant="secondary" icon={<Receipt />} onPress={() => navigate("abrechnung")}>
              {t("console.billing.title")}
            </Button>
            <Button variant="secondary" icon={<Users />} onPress={() => navigate("mitarbeitende")}>
              {t("console.staff.title")}
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
